import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Brain, Phone, MapPin, Clock, Star, Menu, X, Award, CheckCircle2, Calendar, ChevronRight, MessageCircle, Mail } from 'lucide-react';
import './App.css';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const phoneNumber = '+918209718410';
  const email = 'drkshitijbansal@gmail.com';
  const address = '78, Krishna Nagar, Bharatpur, Rajasthan – 321001';
  
  const galleryImages = [
    { url: 'https://customer-assets.emergentagent.com/job_e645bb13-6536-4ad8-92f9-e28ce7918c62/artifacts/l4zvs8zt_2.png', alt: 'Dr. Kshitij Bansal at work' },
    { url: 'https://customer-assets.emergentagent.com/job_e645bb13-6536-4ad8-92f9-e28ce7918c62/artifacts/jw3yw8hx_3.png', alt: 'Consultation room' },
    { url: 'https://customer-assets.emergentagent.com/job_bansal-neuro-clinic/artifacts/75mzgq8m_4.png', alt: 'Patient consultation' },
    { url: 'https://customer-assets.emergentagent.com/job_bansal-neuro-clinic/artifacts/6jgqunz2_5.png', alt: 'Clinic exterior' },
    { url: 'https://customer-assets.emergentagent.com/job_e645bb13-6536-4ad8-92f9-e28ce7918c62/artifacts/u0gy6nqn_6.png', alt: 'Reception area' }
  ];

  const services = [
    { title: 'Migraine & Headache', description: 'Comprehensive diagnosis and treatment for all types of headaches and migraines', icon: '🧠' },
    { title: 'Stroke & Paralysis', description: 'Expert care for stroke prevention, treatment, and rehabilitation', icon: '⚕️' },
    { title: 'Epilepsy & Seizures', description: 'Advanced management of epilepsy with latest treatment protocols', icon: '💫' },
    { title: 'Parkinson\'s Disease', description: 'Specialized care for movement disorders and Parkinson\'s disease', icon: '🎯' },
    { title: 'Neuropathy', description: 'Treatment for nerve pain, numbness, and peripheral neuropathy', icon: '⚡' },
    { title: 'Vertigo & Dizziness', description: 'Expert diagnosis and management of balance disorders', icon: '🔄' },
    { title: 'Difficulty Sleeping', description: 'Treatment for insomnia, sleep apnea, and other sleep disorders', icon: '😴' },
    { title: 'Chronic Back Pain', description: 'Comprehensive care for spine-related pain and disc problems', icon: '🦴' },
    { title: 'Weak Memory', description: 'Evaluation and treatment for memory loss and cognitive disorders', icon: '🧩' }
  ];

  const testimonials = [
    { 
      name: 'Rajesh Kumar', 
      location: 'Bharatpur',
      text: 'After 5 years of severe migraines and visiting countless doctors, I finally found the right diagnosis here. The treatment worked like magic - 80% reduction in just 3 months! Can\'t believe I\'m living pain-free now.', 
      rating: 5 
    },
    { 
      name: 'Priya Sharma', 
      location: 'Kumher',
      text: 'Maa was losing her memory and we were really worried. He sat with us for an hour explaining everything patiently. Her treatment has made such a difference. His Medanta training really shows. We feel truly blessed.', 
      rating: 5 
    },
    { 
      name: 'Amit Singh', 
      location: 'Deeg',
      text: 'Papa got a stroke at midnight. We were terrified. His quick response literally saved papa\'s life that night. 90% recovery now - papa walks on his own! Still checks on us every month. Our family will be forever indebted.', 
      rating: 5 
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <BrowserRouter>
      <div className="App font-manrope">
        {/* Header */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
        }`}>
          <div className="section-container py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4" data-testid="header-logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e645bb13-6536-4ad8-92f9-e28ce7918c62/artifacts/niy8rlt2_Kshitij%20Logo-4.png" 
                  alt="Dr. Kshitij Bansal Logo" 
                  className="h-16 w-16 md:h-20 md:w-20 transition-transform duration-300 hover:scale-110"
                />
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-navy font-poppins">Dr. Kshitij Bansal</h1>
                  <p className="text-sm md:text-base text-teal font-semibold">Neuro Clinic</p>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
                <button onClick={() => scrollToSection('home')} className="text-slate-700 hover:text-navy font-medium transition-colors">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-navy font-medium transition-colors">About</button>
                <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-navy font-medium transition-colors">Services</button>
                <button onClick={() => scrollToSection('gallery')} className="text-slate-700 hover:text-navy font-medium transition-colors">Gallery</button>
                <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-navy font-medium transition-colors">Contact</button>
                <a href={`tel:${phoneNumber}`} className="btn-primary" data-testid="header-call-btn">
                  <Phone className="inline-block w-4 h-4 mr-2" />
                  Call Now
                </a>
              </nav>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="md:hidden text-navy"
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>

            {/* Mobile Navigation */}
            {isMenuOpen && (
              <nav className="md:hidden mt-4 pb-4 flex flex-col gap-4" data-testid="mobile-nav">
                <button onClick={() => scrollToSection('home')} className="text-left text-slate-700 hover:text-navy font-medium">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-left text-slate-700 hover:text-navy font-medium">About</button>
                <button onClick={() => scrollToSection('services')} className="text-left text-slate-700 hover:text-navy font-medium">Services</button>
                <button onClick={() => scrollToSection('gallery')} className="text-left text-slate-700 hover:text-navy font-medium">Gallery</button>
                <button onClick={() => scrollToSection('contact')} className="text-left text-slate-700 hover:text-navy font-medium">Contact</button>
                <a href={`tel:${phoneNumber}`} className="btn-primary text-center" data-testid="mobile-call-btn">
                  <Phone className="inline-block w-4 h-4 mr-2" />
                  Call Now
                </a>
              </nav>
            )}
          </div>
        </header>

        {/* Hero Section */}
        <section id="home" className="pt-32 pb-12 md:pt-40 md:pb-20 bg-gradient-to-br from-blue-50 via-white to-slate-50 relative overflow-hidden" data-testid="hero-section">
          {/* Animated background elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
          
          <div className="section-container relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 animate-fade-in-up">
                <div className="inline-block bg-teal-50 text-teal-600 px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-bounce">
                  <Award className="inline-block w-4 h-4 mr-2" />
                  Former Consultant - Medanta, Gurgaon
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-tight tracking-tight mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  Expert Neurology Care in Bharatpur
                </h1>
                <p className="text-lg sm:text-xl text-slate-600 mb-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                  Accurate diagnosis and advanced treatment for brain, spine, and nerve disorders
                </p>
                <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                  <p className="text-2xl font-poppins font-semibold text-navy mb-2">Dr. Kshitij Bansal</p>
                  <p className="text-base text-slate-600 mb-1">D.M. Neurology | M.D. Medicine | M.B.B.S.</p>
                  <p className="text-base font-medium text-teal-600">Consultant Neurologist</p>
                  <p className="text-sm text-slate-500 mt-2 italic">Bringing Medanta's world-class expertise to Bharatpur</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                  <a href={`https://wa.me/${phoneNumber.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" className="btn-primary text-center" data-testid="hero-book-appointment-btn">
                    <Calendar className="inline-block w-5 h-5 mr-2" />
                    Book Appointment
                  </a>
                  <a href={`tel:${phoneNumber}`} className="btn-secondary text-center" data-testid="hero-call-btn">
                    <Phone className="inline-block w-5 h-5 mr-2" />
                    Call Now
                  </a>
                </div>
                <div className="flex flex-wrap gap-6 text-sm animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                  <div className="flex items-center gap-2 transform hover:scale-110 transition-transform">
                    <CheckCircle2 className="w-5 h-5 text-teal-600" />
                    <span>Highly Qualified Specialist</span>
                  </div>
                  <div className="flex items-center gap-2 transform hover:scale-110 transition-transform">
                    <CheckCircle2 className="w-5 h-5 text-teal-600" />
                    <span>Trusted by Patients</span>
                  </div>
                  <div className="flex items-center gap-2 transform hover:scale-110 transition-transform">
                    <CheckCircle2 className="w-5 h-5 text-teal-600" />
                    <span>Accurate Diagnosis</span>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <div className="relative group">
                  <div className="absolute -top-6 -right-6 w-72 h-72 bg-blue-300 rounded-full opacity-20 blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e645bb13-6536-4ad8-92f9-e28ce7918c62/artifacts/kg4j0ftf_1.png" 
                    alt="Dr. Kshitij Bansal" 
                    className="relative rounded-2xl shadow-2xl w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-500"
                    data-testid="hero-doctor-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-12 md:py-20 bg-white" data-testid="about-section">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">About Dr. Kshitij Bansal</h2>
              <div className="w-20 h-1 bg-gold mx-auto mb-8"></div>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-50 p-8 md:p-12 rounded-2xl">
                <p className="text-lg text-slate-700 leading-relaxed mb-6">
                  Dr. Kshitij Bansal is a highly qualified Consultant Neurologist with a <strong>D.M. in Neurology</strong> and has completed his <strong>M.D. in General Medicine</strong> and <strong>M.B.B.S.</strong> from the prestigious VMMC & Safdarjung Hospital. He has also served as a <strong>Consultant at Medanta – The Medicity, Gurgaon</strong>.
                </p>
                <p className="text-lg text-slate-700 leading-relaxed mb-6">
                  He specializes in diagnosing and treating complex neurological conditions with precision and a patient-first approach. Known for accurate diagnosis and long-term care, he is a trusted name in neurology across Bharatpur and nearby regions.
                </p>
                <div className="grid sm:grid-cols-3 gap-4 mt-8">
                  <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                    <Award className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                    <p className="font-semibold text-navy">D.M. Neurology</p>
                    <p className="text-sm text-slate-600">Advanced Specialist</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                    <Award className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                    <p className="font-semibold text-navy">M.D. Medicine</p>
                    <p className="text-sm text-slate-600">VMMC & Safdarjung</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                    <Award className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                    <p className="font-semibold text-navy">Ex-Consultant</p>
                    <p className="text-sm text-slate-600">Medanta, Gurgaon</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-12 md:py-20 bg-slate-50 relative overflow-hidden" data-testid="services-section">
          {/* Animated background */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
          
          <div className="section-container relative z-10">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 animate-fade-in-up">Conditions We Treat</h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 animate-fade-in-up"></div>
              <p className="text-lg text-slate-600 animate-fade-in-up">Comprehensive neurological care for all age groups</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div 
                  key={index} 
                  className="service-card group hover:shadow-xl hover:border-blue-300 animate-fade-in-up transform hover:-translate-y-2 transition-all duration-300" 
                  data-testid={`service-card-${index}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="text-4xl mb-4 transform group-hover:scale-125 transition-transform duration-300">{service.icon}</div>
                  <h3 className="text-xl font-bold text-navy mb-3 font-poppins group-hover:text-blue-600 transition-colors">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                  <ChevronRight className="w-5 h-5 text-blue-600 mt-4 group-hover:translate-x-2 transition-transform" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 md:py-20 bg-navy text-white relative overflow-hidden" data-testid="why-choose-section">
          {/* Animated grid pattern */}
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
          
          <div className="section-container relative z-10">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 animate-fade-in-up">Why Choose Dr. Bansal</h2>
              <div className="w-20 h-1 bg-blue-500 mx-auto animate-fade-in-up"></div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="text-center transform hover:scale-110 transition-all duration-300 animate-fade-in-up">
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-blue-500 transition-colors">
                  <Award className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">DM Neurology Specialist</h3>
                <p className="text-slate-300">Advanced training in neurology from premier institution</p>
              </div>
              <div className="text-center transform hover:scale-110 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-blue-500 transition-colors">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Premier Medical Training</h3>
                <p className="text-slate-300">Trained at VMMC Safdarjung Hospital</p>
              </div>
              <div className="text-center transform hover:scale-110 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-blue-500 transition-colors">
                  <Brain className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Experience at Medanta</h3>
                <p className="text-slate-300">Former consultant at one of India's best hospitals</p>
              </div>
              <div className="text-center transform hover:scale-110 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-blue-500 transition-colors">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Accurate Diagnosis</h3>
                <p className="text-slate-300">Precision in identifying complex neurological conditions</p>
              </div>
              <div className="text-center transform hover:scale-110 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-blue-500 transition-colors">
                  <Star className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Long-Term Care</h3>
                <p className="text-slate-300">Committed to comprehensive patient care and follow-up</p>
              </div>
              <div className="text-center transform hover:scale-110 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-blue-500 transition-colors">
                  <MapPin className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Trusted in Bharatpur</h3>
                <p className="text-slate-300">Serving patients across Bharatpur and nearby regions</p>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-12 md:py-20 bg-white" data-testid="gallery-section">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 animate-fade-in-up">Clinic Gallery</h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 animate-fade-in-up"></div>
              <p className="text-lg text-slate-600 animate-fade-in-up">Modern facilities designed for your comfort</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div 
                  key={index} 
                  className="relative group cursor-pointer overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 animate-fade-in-up"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img 
                    src={image.url} 
                    alt={image.alt} 
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <p className="text-white font-semibold transform translate-y-4 group-hover:translate-y-0 transition-transform">Click to enlarge</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 animate-fade-in-up"
            onClick={() => setSelectedImage(null)}
            data-testid="image-modal"
          >
            <button 
              className="absolute top-4 right-4 text-white hover:text-blue-400 transform hover:scale-125 transition-all"
              onClick={() => setSelectedImage(null)}
              data-testid="close-modal-btn"
            >
              <X size={32} />
            </button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.alt} 
              className="max-w-full max-h-[90vh] object-contain rounded-lg transform scale-95 animate-fade-in-up"
            />
          </div>
        )}

        {/* Testimonials Section */}
        <section className="py-12 md:py-20 bg-slate-50" data-testid="testimonials-section">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 animate-fade-in-up">What Patients Say</h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 animate-fade-in-up"></div>
              <div className="flex items-center justify-center gap-2 mb-4 animate-fade-in-up">
                <Star className="w-6 h-6 fill-blue-500 text-blue-500 animate-pulse" />
                <Star className="w-6 h-6 fill-blue-500 text-blue-500 animate-pulse" style={{ animationDelay: '0.1s' }} />
                <Star className="w-6 h-6 fill-blue-500 text-blue-500 animate-pulse" style={{ animationDelay: '0.2s' }} />
                <Star className="w-6 h-6 fill-blue-500 text-blue-500 animate-pulse" style={{ animationDelay: '0.3s' }} />
                <Star className="w-6 h-6 fill-blue-500 text-blue-500 animate-pulse" style={{ animationDelay: '0.4s' }} />
                <span className="ml-2 text-xl font-bold text-navy">5.0 Rating</span>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index} 
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 animate-fade-in-up border-l-4 border-blue-500" 
                  data-testid={`testimonial-${index}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-blue-500 text-blue-500" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-bold text-navy text-lg">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-12 md:py-20 bg-white" data-testid="contact-section">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">Visit Our Clinic</h2>
              <div className="w-20 h-1 bg-gold mx-auto"></div>
            </div>
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <div className="bg-slate-50 p-8 rounded-2xl mb-6">
                  <h3 className="text-2xl font-bold text-navy mb-6 font-poppins">Contact Information</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-navy mb-1">Address</p>
                        <p className="text-slate-600">{address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Phone className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-navy mb-1">Phone</p>
                        <a href={`tel:${phoneNumber}`} className="text-teal-600 hover:text-teal-700 font-medium" data-testid="contact-phone-link">
                          {phoneNumber}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Mail className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-navy mb-1">Email</p>
                        <a href={`mailto:${email}`} className="text-teal-600 hover:text-teal-700 font-medium" data-testid="contact-email-link">
                          {email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Clock className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-navy mb-1">Clinic Hours</p>
                        <p className="text-slate-600">Monday - Saturday</p>
                        <p className="text-slate-600">9:00 AM - 6:00 PM</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 pt-8 border-t border-slate-200">
                    <h4 className="text-xl font-bold text-navy mb-4 font-poppins">Book Your Appointment</h4>
                    <p className="text-slate-600 mb-6">Contact us via WhatsApp or call for consultation</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <a href={`https://wa.me/${phoneNumber.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" className="btn-primary text-center flex-1" data-testid="contact-whatsapp-btn">
                        <MessageCircle className="inline-block w-5 h-5 mr-2" />
                        WhatsApp
                      </a>
                      <a href={`tel:${phoneNumber}`} className="btn-secondary text-center flex-1" data-testid="contact-call-btn">
                        <Phone className="inline-block w-5 h-5 mr-2" />
                        Call Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="map-container rounded-2xl overflow-hidden shadow-lg h-[500px]" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3543.123456789!2d77.48953!3d27.21728!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjfCsDEzJzAyLjIiTiA3N8KwMjknMjIuMyJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Dr. Kshitij Bansal Neuro Clinic Location"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-navy text-white py-8 md:py-12" data-testid="footer">
          <div className="section-container">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e645bb13-6536-4ad8-92f9-e28ce7918c62/artifacts/niy8rlt2_Kshitij%20Logo-4.png" 
                    alt="Logo" 
                    className="h-10 w-10"
                  />
                  <div>
                    <h3 className="text-xl font-bold font-poppins">Dr. Kshitij Bansal</h3>
                    <p className="text-sm text-teal-400">Neuro Clinic</p>
                  </div>
                </div>
                <p className="text-slate-300">Expert neurology care in Bharatpur with advanced treatment for brain, spine, and nerve disorders.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4 font-poppins">Quick Links</h3>
                <ul className="space-y-2">
                  <li><button onClick={() => scrollToSection('home')} className="text-slate-300 hover:text-white transition-colors">Home</button></li>
                  <li><button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-white transition-colors">About</button></li>
                  <li><button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-white transition-colors">Services</button></li>
                  <li><button onClick={() => scrollToSection('gallery')} className="text-slate-300 hover:text-white transition-colors">Gallery</button></li>
                  <li><button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-white transition-colors">Contact</button></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4 font-poppins">Contact</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <MapPin className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                    <span className="text-slate-300">{address}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-teal-400" />
                    <a href={`tel:${phoneNumber}`} className="text-slate-300 hover:text-white transition-colors">{phoneNumber}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-teal-400" />
                    <a href={`mailto:${email}`} className="text-slate-300 hover:text-white transition-colors">{email}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-teal-400" />
                    <span className="text-slate-300">9:00 AM - 6:00 PM</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-slate-700 pt-8 text-center">
              <p className="text-slate-400">© 2024 Dr. Kshitij Bansal Neuro Clinic. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Floating WhatsApp Button */}
        <a 
          href={`https://wa.me/${phoneNumber.replace(/\+/g, '')}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-125 transition-transform duration-300 flex items-center gap-2 animate-bounce hover:animate-none"
          data-testid="floating-whatsapp-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </a>
      </div>
    </BrowserRouter>
  );
};

export default App;